import { useState, useEffect, useCallback, useRef } from 'react';
import { INSTAGRAM_URL } from '../../config/site';

interface HeroSlide {
  id: string;
  heading: string[];
  cta: { label: string; href: string };
}

const HERO_SLIDES: HeroSlide[] = [
  {
    id: 'letter-number',
    heading: ['LETTER & NUMBER', 'CAKES'],
    cta: { label: 'ORDER ON INSTAGRAM', href: INSTAGRAM_URL },
  },
  {
    id: 'spell-it',
    heading: ['SPELL A NAME', 'OR AN AGE'],
    cta: { label: 'ORDER ON INSTAGRAM', href: INSTAGRAM_URL },
  },
  {
    id: 'made-fresh',
    heading: ['MADE FRESH', 'IN SAN FRANCISCO'],
    cta: { label: 'ORDER ON INSTAGRAM', href: INSTAGRAM_URL },
  },
];

const AUTO_ADVANCE_MS = 6000;
const SWIPE_THRESHOLD = 50;

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <rect x="3" y="2" width="4" height="12" rx="1" />
      <rect x="9" y="2" width="4" height="12" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M4 2l10 6-10 6V2z" />
    </svg>
  );
}

function SoundIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M2 6v4h3l4 3V3L5 6H2z" />
        <path d="M13 5l-4 6M9 5l4 6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 6v4h3l4 3V3L5 6H2z" />
      <path d="M11 5.5a3 3 0 010 5" strokeLinecap="round" />
    </svg>
  );
}

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const advance = useCallback(() => {
    setCurrent((prev) => (prev + 1) % HERO_SLIDES.length);
  }, []);

  const goTo = useCallback((index: number) => {
    setCurrent((index + HERO_SLIDES.length) % HERO_SLIDES.length);
    // reset timer on manual nav
    if (timerRef.current) clearInterval(timerRef.current);
    if (!paused) {
      timerRef.current = setInterval(advance, AUTO_ADVANCE_MS);
    }
  }, [paused, advance]);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(advance, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, advance]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    goTo(deltaX < 0 ? current + 1 : current - 1);
  };

  const slide = HERO_SLIDES[current];

  return (
    <section
      className="relative w-full h-screen overflow-hidden bg-card-bg"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Hero background image */}
      <div className="absolute inset-0">
        <img
          src="/images/hero-cake.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
      </div>

      {/* Overlay content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
        <div className="text-center px-4">
          {slide.heading.map((line, i) => (
            <h1
              key={`${slide.id}-${i}`}
              className="font-display font-normal text-4xl md:text-6xl lg:text-7xl tracking-[0.15em] leading-tight m-0 text-[#F5F0EB]"
            >
              {line}
            </h1>
          ))}
          <p className="font-inter text-xs md:text-sm tracking-[0.2em] uppercase text-white/80 mt-6 max-w-md mx-auto leading-relaxed">
            Custom cream-tart cakes for the moments that matter
          </p>
        </div>
        <a
          href={slide.cta.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 font-inter text-xs tracking-[0.2em] uppercase text-white no-underline border-b border-white pb-1 hover:opacity-60 transition-opacity pointer-events-auto"
        >
          {slide.cta.label}
        </a>
      </div>

      {/* Controls: bottom-left */}
      <div className="absolute bottom-6 left-6 flex items-center gap-3 z-20">
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          className="w-8 h-8 rounded-full bg-theme-font/20 text-theme-font flex items-center justify-center border-0 cursor-pointer hover:bg-theme-font/30 transition-colors"
          aria-label={paused ? 'Play' : 'Pause'}
        >
          {paused ? <PlayIcon /> : <PauseIcon />}
        </button>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          className="w-8 h-8 rounded-full bg-theme-font/20 text-theme-font flex items-center justify-center border-0 cursor-pointer hover:bg-theme-font/30 transition-colors"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          <SoundIcon muted={muted} />
        </button>
      </div>

      {/* Pagination dots: bottom-center */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full border-0 cursor-pointer transition-all ${
              i === current
                ? 'bg-theme-font scale-125'
                : 'bg-theme-font/30 hover:bg-theme-font/60'
            }`}
            aria-label={`Slide ${i + 1} of ${HERO_SLIDES.length}`}
          />
        ))}
      </div>
    </section>
  );
}
